import {
  Button,
  Card,
  Checkbox,
  Col,
  ConfigProvider,
  Flex,
  Form,
  FormItemProps,
  Modal,
  Radio,
  Row,
  Select,
  Spin,
  Table
} from "antd";
import { addOrUpdateVTUser, getDialedNumbers, getVTUser } from "api/services/ContactService";
import {
  KeyFunction,
  TContact,
  TLocationPicklist,
  TVTDialedNumberMap,
  TVTUser
} from "api/services/models/types";

import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import { Header } from "components/common/Header";
import NumericStringInput from "components/common/NumericStringInput";
import ErrorViewer from "components/ErrorViewer";
import { usePicklists } from "components/picklists/PicklistContextProvider";
import { useEffect, useState } from "react";
import { validateVTUserId } from "../users/validators";
import SearchAvailableVTUsers from "./SearchAvailableVTUsers";
import { keypad1, keypad2 } from "./Utils";

type TAddOrUpdateVTUserProps = {
  contact: TContact;
  onClose: (userId?: string) => void;
};

const columns = [
  {
    title: "Key",
    dataIndex: "key"
  },
  {
    title: "Function",
    dataIndex: "function"
  },
  {
    title: "Description",
    dataIndex: "description"
  }
];

type TFormRow = {
  key: number;
  gutter: number;
  columns: (FormItemProps & { size: number; key: string | number; offset?: number })[];
};
type TVTUserEdit = TVTUser & {
  promptForPassword: boolean;
  promptForJobType: boolean;
  promptForLocation: boolean;
};

const keyPadOptions = [
  { value: 1, label: "Key Pad 1" },
  { value: 2, label: "Key Pad 2" }
];

const makeVTFields = (
  vtUser: TVTUser | null,
  locations: TLocationPicklist[] | undefined,
  tollFreeNumbers: TVTDialedNumberMap[] | undefined,
  openSearchModal: () => void,
  webDUser: any
) => {
  const vtFields: TFormRow[] = [
    {
      key: 1,
      gutter: 4,
      columns: [
        {
          name: "userId",
          label: "User Id",
          size: 10,
          key: 1,
          layout: "horizontal",
          children: (
            <NumericStringInput
              minLength={3}
              maxLength={8}
              disabled={vtUser != null}
              autoComplete="false"
              aria-autocomplete="none"
            />
          ),
          dependencies: ["dialedNumber"],
          rules: [
            { required: true, message: "Required!" },
            { min: 3, message: "min: 3 digits" },
            { max: 8, message: "max: 8 digits" },
            validateVTUserId(vtUser)
          ]
        },
        {
          name: "dialedNumber",
          label: "Dial Number",
          size: 14,
          key: 2,
          labelAlign: "right",
          children: (
            <Select
              fieldNames={{ label: "tollFreeNumber", value: "dialedNumber" }}
              options={tollFreeNumbers}
            />
          )
        }
      ]
    },
    {
      key: 33,
      gutter: 4,
      columns: [
        {
          size: 4,
          offset: 3,
          key: 3,
          children: (
            <Button
              type="link"
              onClick={openSearchModal}
              hidden={vtUser != null}
              disabled={!webDUser.isInternalUser()}>
              Lookup
            </Button>
          )
        }
      ]
    },
    {
      key: 3,
      gutter: 4,
      columns: [
        {
          name: "promptForPassword",
          size: 10,
          key: 4,
          shouldUpdate: true,
          children: <Checkbox>Prompt for password</Checkbox>,
          valuePropName: "checked"
        },
        {
          size: 14,
          key: 5,
          shouldUpdate: (old, current) => old.promptForPassword !== current.promptForPassword,
          children: ({ getFieldValue }) =>
            getFieldValue("promptForPassword") ? (
              <Form.Item
                name="password"
                label="Password"
                labelAlign="right"
                labelCol={{ span: 12 }}
                wrapperCol={{ span: 12 }}
                rules={[
                  { required: true, message: "Required!" },
                  { min: 3, message: "min: 3 digits" },
                  { max: 8, message: "max: 8 digits" }
                ]}>
                <NumericStringInput autoComplete="false" aria-autocomplete="none" />
              </Form.Item>
            ) : null
        }
      ]
    },
    {
      key: 4,
      gutter: 4,
      columns: [
        {
          name: "promptForJobType",
          size: 10,
          key: 6,
          children: <Checkbox>Prompt for Job type</Checkbox>,
          valuePropName: "checked"
        },
        {
          size: 14,
          key: 7,
          shouldUpdate: (old, current) => old.promptForJobType !== current.promptForJobType,
          children: ({ getFieldValue }) =>
            !getFieldValue("promptForJobType") && (
              <Form.Item
                name="defaultJobType"
                label="Default Job Type"
                labelAlign="right"
                labelCol={{ span: 12 }}
                wrapperCol={{ span: 12 }}
                rules={[{ required: true, message: "Required" }]}>
                <NumericStringInput
                  autoComplete="false"
                  aria-autocomplete="none"
                  max={32767}
                  min={0}
                />
              </Form.Item>
            )
        }
      ]
    },
    {
      key: 5,
      gutter: 4,
      columns: [
        {
          name: "promptForLocation",
          size: 10,
          key: 8,
          children: <Checkbox>Prompt for Location</Checkbox>,
          valuePropName: "checked"
        },
        {
          size: 14,
          key: 9,
          shouldUpdate: (old, current) => old.promptForLocation !== current.promptForLocation,
          children: ({ getFieldValue }) =>
            !getFieldValue("promptForLocation") && (
              <Form.Item
                name="defaultLocation"
                label="Default Location"
                labelAlign="right"
                labelCol={{ span: 12 }}
                wrapperCol={{ span: 12 }}
                rules={[{ required: true }]}>
                <Select
                  placeholder="Please select a location"
                  fieldNames={{ label: "locationName", value: "locationId" }}
                  options={locations}
                />
              </Form.Item>
            )
        }
      ]
    },
    {
      key: 6,
      gutter: 16,
      columns: [
        {
          name: "promptForMRN",
          size: 24,
          key: 10,
          children: <Checkbox>Prompt for MRN</Checkbox>,
          valuePropName: "checked"
        }
      ]
    },
    {
      key: 7,
      gutter: 16,
      columns: [
        {
          name: "saveOnHangup",
          size: 24,
          key: 11,
          children: <Checkbox>Save on hangup</Checkbox>,
          valuePropName: "checked"
        }
      ]
    }
  ];
  return vtFields;
};

const newVTInitialValues = {
  promptForPassword: true,
  promptForJobType: true,
  promptForLocation: true,
  promptForMRN: true,
  saveOnHangup: true,
  dialedNumber: "5426"
};

const AddOrUpdateVTUser = ({ contact, onClose }: Readonly<TAddOrUpdateVTUserProps>) => {
  const { accountId } = useTDAuthentication();
  const { locations } = usePicklists();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>();
  const [form] = Form.useForm();

  const [vtUser, setVtUser] = useState<TVTUser | null>(null);
  const [dialedNumbersMap, setDialedNumbersMap] = useState<TVTDialedNumberMap[]>();
  const [keypad, setKeypad] = useState(1);
  const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
  const { webDUser } = useTDAuthentication();

  useEffect(() => {
    if (accountId) {
      setLoading(true);
      setError(null);
      Promise.all([
        contact.isVTUser ? getVTUser(accountId, contact.contactId) : Promise.resolve(null),
        getDialedNumbers()
      ])
        .then(results => {
          setDialedNumbersMap(results[1]);
          if (results[0]) {
            setVtUser(results[0]);
          }
        })
        .catch(setError)
        .finally(() => setLoading(false));
    }
  }, []);

  useEffect(() => {
    if (vtUser) {
      setKeypad(vtUser.isOratorUser ? 1 : 2);
      form.setFieldsValue({
        ...vtUser,
        promptForPassword: vtUser.password != null,
        promptForJobType: !vtUser.defaultJobType,
        promptForLocation: !vtUser.defaultLocation
      });
    }
  }, [vtUser]);

  const onFormValuesChange = (values: Partial<TVTUser>) => {
    if ("keypad" in values) {
      setKeypad(values["keypad"] as number);
    }
  };

  const openSearchModal = () => setIsSearchModalVisible(true);
  const closeSearchModal = (userId?: string, dialedNumber?: string) => {
    if (userId != null && dialedNumber != null) {
      form.setFieldsValue({ userId, dialedNumber });
    }
    setIsSearchModalVisible(false);
  };

  const onSave = () => {
    form
      .validateFields()
      .then(() => form.submit())
      .catch(setError);
  };

  const onFinish = ({
    promptForLocation,
    promptForPassword,
    promptForJobType,
    ...transformedValues
  }: TVTUserEdit) => {
    const vtUserTransformed: TVTUser = {
      ...vtUser,
      ...transformedValues,
      accountId: contact.accountId,
      contactId: contact.contactId
    };

    vtUserTransformed.password = promptForPassword ? vtUserTransformed.password : null;
    vtUserTransformed.defaultJobType = promptForJobType ? 0 : vtUserTransformed.defaultJobType;
    vtUserTransformed.defaultLocation = promptForLocation
      ? null
      : vtUserTransformed.defaultLocation;
    vtUserTransformed.isOratorUser = keypad == 1;
    keypad1.forEach(k => Object.assign(vtUserTransformed, { [k.name]: k.key }));

    setLoading(true);
    setError(null);

    addOrUpdateVTUser(vtUserTransformed, !contact.isVTUser)
      .then(onClose)
      .catch(setError)
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      width={540}
      maskClosable={false}
      title={
        <Header
          header={`New VT User - ${contact.contactName}`}
          editHeader={`Edit VT User - ${contact.contactName}`}
          onSubmit={onSave}
          onClose={onClose}
        />
      }
      open={true}
      footer={null}
      closeIcon={null}
      onCancel={() => onClose()}
      destroyOnClose={true}>
      {loading && (
        <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
          <Spin tip="Saving, please wait..." />
        </Flex>
      )}
      {error && <ErrorViewer error={error} />}
      {!loading && <>
          <Form<TVTUserEdit>
            form={form}
            name="add_or_update_vtuser"
            size="small"
            onFinish={onFinish}
            colon={false}
            validateTrigger={["onBlur"]}
            initialValues={newVTInitialValues}
            onValuesChange={onFormValuesChange}>
            {makeVTFields(vtUser, locations, dialedNumbersMap, openSearchModal, webDUser).map(
              row => (
                <Row gutter={row.gutter} key={row.key}>
                  {row.columns.map(col => (
                    <Col key={col.key} span={col.size} offset={col.offset}>
                      <Form.Item {...col}>{col.children}</Form.Item>
                    </Col>
                  ))}
                </Row>
              )
            )}
          </Form>
          <Flex style={{ margin: "8px 0" }} justify="space-around">
            <Radio.Group
              size="large"
              onChange={e => setKeypad(e.target.value)}
              value={keypad}
              options={keyPadOptions}
            />
          </Flex>
          <Table<KeyFunction>
            columns={columns}
            dataSource={keypad == 2 ? keypad2 : keypad1}
            pagination={false}
            bordered={false}
            className="td-table"
          />
          </>
        }
      {isSearchModalVisible && <SearchAvailableVTUsers onClose={closeSearchModal} />}
    </Modal>
  );
};

export default AddOrUpdateVTUser;
